import { Link } from 'react-router-dom';
import type { SolConversationSourceCard } from '../../data/sol/sol-contract';
import { formatInstant } from '../../lib/timezone';
import { solConversationPath } from './sol-routes';
import { conversationOriginLabel, UNTITLED_CONVERSATION } from './sol-wording';

/**
 * One cited EARLIER CONVERSATION, as a real link that reopens it. M11 / D-041.
 *
 * **PROVENANCE FOR A RECALL ANSWER.** When Sol says "we decided the drywall
 * goes first", this card is what that rests on: the conversation's title,
 * where it was started, when it was last active, and the bounded excerpt Sol
 * was shown. He can open it and read the whole thing.
 *
 * **IT IS A CONVERSATION, AND LOOKS LIKE ONE.** A dashed edge, deliberately
 * unlike the blue edge of a cited record: this is what was SAID on an earlier
 * day, not the current state of anything. Nothing on it is re-read from a
 * Capture or a Project, and no UUID is rendered or spoken.
 */

export interface SolConversationSourceCardLinkProps {
  readonly card: SolConversationSourceCard;
  /** The canonical Crewlink timezone, or null → no timestamp (INV-T17). */
  readonly timeZone: string | null;
}

export function SolConversationSourceCardLink({
  card,
  timeZone,
}: SolConversationSourceCardLinkProps) {
  const title = card.title?.trim() || UNTITLED_CONVERSATION;
  const origin = conversationOriginLabel(card.projectName, card.startedContext === 'global');
  const when = timeZone
    ? formatInstant(card.lastActivityAt, timeZone, { dateStyle: 'medium' })
    : '';

  return (
    <Link
      className="sol-source sol-source--conversation"
      to={solConversationPath(card.projectId, card.source.id)}
      aria-label={`Open earlier conversation ${title}`}
    >
      <span className="sol-source-head">
        <span className="sol-source-title">{title}</span>
        <span className="sol-source-status">EARLIER</span>
      </span>
      <span className="sol-source-meta">
        {origin}
        {when ? ` · ${when}` : ''}
      </span>
      <span className="sol-source-caption">{card.excerpt}</span>
      <span className="sol-source-open" aria-hidden="true">
        Open conversation →
      </span>
    </Link>
  );
}
